/** TodoPendingPopup.tsx — 로그인 시 내가 담당자이거나 의뢰한 미완료 팝업 */
import { useState, useEffect } from 'react';
import { C, T, R } from './ui';
import * as todosApi from '../api-todos';
import type { Todo } from '../types';
import TodoCard from './TodoCard';
import { stageOf } from '../todoCheckView';
import TodoDetailDrawer from './TodoDetailDrawer';
import type { Staff } from './AssigneeChipPicker';

interface Props {
  myId: string;
  onClose: () => void;
  onGoToTodos: () => void;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
}

export default function TodoPendingPopup({ myId, onClose, onGoToTodos, onToast }: Props) {
  const [items, setItems] = useState<Todo[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [doneCount, setDoneCount] = useState(0);
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      todosApi.getPendingForMe(),
      todosApi.getStaff(),
    ]).then(([pr, sr]) => {
      if (pr.success && pr.data) setItems(pr.data);
      if (sr.success && sr.data) setStaff(sr.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function handleChange(t: Todo) {
    // 끝났거나 · 내 차례가 지나간 결과 확인 일(답 보냄 · 남에게 안내 맡김)은 목록에서 뺀다(2026-10-22)
    const st = stageOf(t, myId);
    if (t.status === 'done' || st === 'waitCheck' || st === 'bossNotifies' || st === 'staffNotifying') {
      setItems(prev => {
        const next = prev.filter(x => x.id !== t.id);
        if (next.length === 0) {
          setTimeout(() => onClose(), 400);
        }
        return next;
      });
      setDoneCount(c => c + 1);
    } else {
      setItems(prev => prev.map(x => x.id === t.id ? t : x));
    }
  }

  function handleDelete(id: string) {
    setItems(prev => prev.filter(x => x.id !== id));
  }

  if (loading) return null;
  if (items.length === 0 && doneCount === 0) return null;

  const overdue = items.filter(t => {
    if (!t.due_date) return false;
    return new Date(t.due_date) < new Date(new Date().toDateString());
  }).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: C.white, borderRadius: R.lg, width: '100%',
        maxWidth: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: T.lg, fontWeight: 800, color: C.text }}>
              📌 오늘 챙겨야 할 일일 업무
              {items.length > 0 && <span style={{ marginLeft: 8, color: C.accent }}>{items.length}건</span>}
            </div>
            {overdue > 0 && (
              <div style={{ fontSize: T.xs, color: C.red, marginTop: 3, fontWeight: 600 }}>
                ⚠ 마감 지난 항목 {overdue}건
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent',
            fontSize: T.xl, cursor: 'pointer', color: C.muted, padding: 4 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '14px 20px' }}>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
              <div style={{ fontSize: T.md, color: C.green, fontWeight: 700 }}>
                오늘 {doneCount}건 완료!
              </div>
              <div style={{ fontSize: T.sm, color: C.muted, marginTop: 5 }}>
                남은 일일 업무 없음
              </div>
            </div>
          ) : (
            <>
              {doneCount > 0 && (
                <div style={{ background: C.greenLt, color: C.green, padding: '8px 12px',
                  borderRadius: R.sm, fontSize: T.sm, fontWeight: 600, marginBottom: 10 }}>
                  🎉 이번 세션 {doneCount}건 완료!
                </div>
              )}
              {items.map(t => (
                <TodoCard key={t.id} todo={t} staff={staff} myId={myId}
                  onChange={handleChange} onDelete={handleDelete}
                  onOpenDetail={(id) => setDetailId(id)}
                  compact onToast={onToast} />
              ))}
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <button onClick={onGoToTodos}
            style={{ padding: '7px 14px', borderRadius: R.sm,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.sub, fontSize: T.sm, cursor: 'pointer' }}>
            전체 보기
          </button>
          <button onClick={onClose}
            style={{ padding: '7px 18px', borderRadius: R.sm, border: 'none',
              background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>
            닫기
          </button>
        </div>
      </div>

      {detailId && (
        <TodoDetailDrawer todoId={detailId} initial={items.find(x => x.id === detailId)} staff={staff} myId={myId}
          onClose={() => setDetailId(null)}
          onUpdated={handleChange} onDeleted={handleDelete}
          onToast={onToast} />
      )}
    </div>
  );
}
